import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useAnalyticsProjects } from '../../hooks/useApi';
import { downloadCSV } from '../../utils/csvExport';
import {
    Avatar,
    AvatarGroup,
    Badge,
    Button,
    Card,
    CardHeader,
    DataTable,
    EmptyState,
    Pagination,
    ProgressBar,
    StatusBadge,
    useSort,
} from '../../components/ui';

const ProjectsTab = ({ filters }) => {
    const [page, setPage] = useState(1);
    const { data: result, isLoading } = useAnalyticsProjects({ ...filters, page });
    const projects = useMemo(() => result?.data || [], [result]);
    const { sort, onSort } = useSort({ key: 'project_name', dir: 'asc' });

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const sortedProjects = useMemo(() => {
        if (!sort) return projects;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...projects].sort((a, b) => {
            let av = a[sort.key];
            let bv = b[sort.key];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [projects, sort]);

    const totalPages = result?.totalPages || 1;
    const total = result?.total || sortedProjects.length;

    const columns = [
        {
            key: 'project_name',
            header: 'Project',
            sortable: true,
            render: (p) => (
                <>
                    <span className="block font-medium text-ink-900 truncate max-w-[240px]">
                        {p.project_name}
                    </span>
                    <span className="block text-micro font-mono text-ink-500 mt-0.5">{p.project_code}</span>
                </>
            ),
        },
        {
            key: 'assignee_name',
            header: 'Project manager',
            sortable: true,
            render: (p) =>
                p.assignee_name ? (
                    <span className="flex items-center gap-2">
                        <Avatar name={p.assignee_name} size="sm" />
                        <span className="text-ink-700 truncate max-w-[130px]">{p.assignee_name}</span>
                    </span>
                ) : (
                    <span className="text-muted">Unassigned</span>
                ),
        },
        {
            key: 'owners',
            header: 'Team',
            render: (p) =>
                p.owners?.length ? (
                    <AvatarGroup names={p.owners.map((o) => o.full_name || o.name || String(o))} max={3} />
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (p) => <StatusBadge status={p.status} />,
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: true,
            width: 150,
            render: (p) => <ProgressBar value={p.progress} showValue />,
        },
        {
            key: 'total_tasks',
            header: 'Tasks',
            sortable: true,
            align: 'right',
            render: (p) => (
                <span className="tabular text-ink-700">
                    <span className="font-medium text-ink-900">{p.completed_tasks}</span>
                    <span className="text-ink-400"> / {p.total_tasks}</span>
                </span>
            ),
        },
        {
            key: 'overdue_tasks',
            header: 'Overdue',
            sortable: true,
            align: 'right',
            render: (p) =>
                p.overdue_tasks > 0 ? (
                    <Badge variant="danger">{p.overdue_tasks}</Badge>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
    ];

    return (
        <Card>
            <CardHeader
                title="Project status"
                subtitle="Delivery progress and overdue exposure per project"
                actions={
                    <Button
                        size="sm"
                        icon={Download}
                        className="no-print"
                        onClick={() => downloadCSV(sortedProjects, 'projects-report.csv')}
                        disabled={!sortedProjects.length}
                    >
                        Export CSV
                    </Button>
                }
            />

            <DataTable
                columns={columns}
                rows={sortedProjects}
                loading={isLoading}
                sort={sort}
                onSort={onSort}
                empty={
                    <EmptyState
                        title="No projects match this selection"
                        description="Adjust the filters or widen the date range."
                    />
                }
            />

            {sortedProjects.length > 0 && totalPages > 1 && (
                <Pagination
                    className="no-print"
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={Math.ceil(total / totalPages)}
                    onPageChange={setPage}
                />
            )}
        </Card>
    );
};

export default ProjectsTab;
